'use client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SupplierStatus =
  | 'draft' | 'pending_review' | 'pending_approval' | 'active' | 'preferred' |'suspended' | 'blacklisted' | 'inactive' | 'rejected' | 'archived';

export type ComplianceStatus = 'compliant' | 'expiring_soon' | 'expired' | 'pending' | 'non_compliant';

export interface SupplierCategory {
  id: string;
  name: string;
}

export interface SupplierContact {
  id: string;
  supplierId: string;
  fullName: string;
  jobTitle?: string;
  department?: string;
  phone?: string;
  altPhone?: string;
  email?: string;
  whatsapp?: string;
  isPrimary: boolean;
  notes?: string;
}

export interface SupplierDocument {
  id: string;
  supplierId: string;
  documentType: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  status: 'valid' | 'expiring_soon' | 'expired' | 'pending_verification' | 'rejected';
  verifiedBy?: string;
  verificationDate?: string;
}

export interface SupplierProduct {
  id: string;
  supplierId: string;
  productName: string;
  supplierCode?: string;
  category: string;
  brand?: string;
  description?: string;
  unit: string;
  standardPrice: number;
  currency: string;
  minOrderQty?: number;
  leadTimeDays?: number;
  lastPurchasePrice?: number;
  lastPurchaseDate?: string;
}

export interface SupplierPerformance {
  supplierId: string;
  qualityScore: number;
  deliveryScore: number;
  priceScore: number;
  complianceScore: number;
  communicationScore: number;
  supportScore: number;
  invoiceAccuracyScore: number;
  overallScore: number;
  onTimeDeliveryPct: number;
  acceptanceRate: number;
  totalOrders: number;
  lateDeliveries: number;
  rejectedDeliveries: number;
}

export interface SupplierEvaluation {
  id: string;
  supplierId: string;
  supplierName: string;
  period: string;
  evaluator: string;
  qualityScore: number;
  deliveryScore: number;
  priceScore: number;
  complianceScore: number;
  communicationScore: number;
  supportScore: number;
  overallScore: number;
  comments?: string;
  recommendations?: string;
  status: 'draft' | 'submitted' | 'approved';
  date: string;
}

export interface SupplierIncident {
  id: string;
  incidentNumber: string;
  supplierId: string;
  supplierName: string;
  date: string;
  poNumber?: string;
  incidentType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  reportedBy: string;
  correctiveAction?: string;
  status: 'open' | 'under_review' | 'resolved' | 'closed';
}

export interface SupplierPayment {
  id: string;
  paymentNumber: string;
  supplierId: string;
  supplierName: string;
  invoiceRef: string;
  paymentDate: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  reference: string;
  processedBy: string;
}

export interface SupplierContract {
  id: string;
  contractNumber: string;
  supplierId: string;
  supplierName: string;
  contractType: string;
  startDate: string;
  endDate: string;
  value: number;
  currency: string;
  paymentTerms: string;
  status: 'draft' | 'active' | 'expiring' | 'expired' | 'terminated' | 'renewed';
  owner: string;
}

export interface Supplier {
  id: string;
  supplierCode: string;
  name: string;
  tradingName?: string;
  supplierType: string;
  categories: string[];
  registrationNumber?: string;
  taxNumber?: string;
  country: string;
  district?: string;
  city?: string;
  physicalAddress?: string;
  email?: string;
  phone?: string;
  website?: string;
  paymentTerms: string;
  creditDays?: number;
  creditLimit?: number;
  currency: string;
  overallScore?: number;
  rating?: number;
  preferred: boolean;
  complianceStatus: ComplianceStatus;
  status: SupplierStatus;
  totalSpend: number;
  outstandingBalance: number;
  openPOs: number;
  onTimeDeliveryPct?: number;
  qualityScore?: number;
  lastPurchaseDate?: string;
  contacts?: SupplierContact[];
  documents?: SupplierDocument[];
  products?: SupplierProduct[];
  performance?: SupplierPerformance;
  createdAt: string;
  updatedAt: string;
}

// ─── Status Styles ────────────────────────────────────────────────────────────

export const SUPPLIER_STATUS_STYLES: Record<SupplierStatus, { bg: string; text: string; dot: string; label: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'Draft' },
  pending_review: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400', label: 'Pending Review' },
  pending_approval: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'Pending Approval' },
  active: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', label: 'Active' },
  preferred: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500', label: 'Preferred' },
  suspended: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500', label: 'Suspended' },
  blacklisted: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', label: 'Blacklisted' },
  inactive: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400', label: 'Inactive' },
  rejected: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-400', label: 'Rejected' },
  archived: { bg: 'bg-gray-100', text: 'text-gray-400', dot: 'bg-gray-300', label: 'Archived' },
};

export const COMPLIANCE_STATUS_STYLES: Record<ComplianceStatus, { bg: string; text: string; label: string }> = {
  compliant: { bg: 'bg-green-50', text: 'text-green-700', label: 'Compliant' },
  expiring_soon: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Expiring Soon' },
  expired: { bg: 'bg-red-50', text: 'text-red-700', label: 'Expired' },
  pending: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Pending' },
  non_compliant: { bg: 'bg-red-50', text: 'text-red-600', label: 'Non-Compliant' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatUGX(amount: number): string {
  return `UGX ${amount.toLocaleString('en-UG')}`;
}

export function getPerformanceLevel(score: number): { label: string; color: string } {
  if (score >= 90) return { label: 'Excellent', color: 'text-green-700' };
  if (score >= 80) return { label: 'Good', color: 'text-blue-700' };
  if (score >= 70) return { label: 'Satisfactory', color: 'text-amber-700' };
  if (score >= 60) return { label: 'Needs Improvement', color: 'text-orange-700' };
  return { label: 'Poor', color: 'text-red-700' };
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

export const MOCK_SUPPLIERS: Supplier[] = [
  {
    id: 'sup-001',
    supplierCode: 'SUP-00001',
    name: 'ABC Feeds Uganda Ltd',
    tradingName: 'ABC Feeds',
    supplierType: 'Manufacturer',
    categories: ['Feed', 'Feed Ingredients', 'Feed Supplements'],
    registrationNumber: 'UG-REG-2018-0045',
    taxNumber: 'TIN-1002345678',
    country: 'Uganda',
    district: 'Kampala',
    city: 'Kampala',
    physicalAddress: 'Plot 45, Industrial Area, Kampala',
    email: 'procurement@abcfeeds.co.ug',
    phone: '+256 700 123 456',
    website: 'www.abcfeeds.co.ug',
    paymentTerms: '30 Days',
    creditDays: 30,
    creditLimit: 50000000,
    currency: 'UGX',
    overallScore: 94,
    rating: 4.7,
    preferred: true,
    complianceStatus: 'compliant',
    status: 'preferred',
    totalSpend: 187500000,
    outstandingBalance: 12500000,
    openPOs: 2,
    onTimeDeliveryPct: 94,
    qualityScore: 96,
    lastPurchaseDate: '2026-08-28',
    contacts: [
      { id: 'c-001', supplierId: 'sup-001', fullName: 'Mukasa David', jobTitle: 'Sales Manager', phone: '+256 700 123 456', email: 'david@abcfeeds.co.ug', isPrimary: true },
      { id: 'c-002', supplierId: 'sup-001', fullName: 'Namukasa Grace', jobTitle: 'Accounts Contact', phone: '+256 701 234 567', email: 'accounts@abcfeeds.co.ug', isPrimary: false },
    ],
    documents: [
      { id: 'd-001', supplierId: 'sup-001', documentType: 'Certificate of Incorporation', documentNumber: 'CI-2018-0045', issueDate: '2018-03-15', expiryDate: '2028-03-15', status: 'valid' },
      { id: 'd-002', supplierId: 'sup-001', documentType: 'Trading License', documentNumber: 'TL-2026-0123', issueDate: '2026-01-01', expiryDate: '2026-12-31', status: 'valid' },
      { id: 'd-003', supplierId: 'sup-001', documentType: 'Tax Clearance', documentNumber: 'TC-2026-0456', issueDate: '2026-01-15', expiryDate: '2026-12-31', status: 'valid' },
    ],
    products: [
      { id: 'p-001', supplierId: 'sup-001', productName: 'Broiler Starter Feed', supplierCode: 'ABC-BS-50', category: 'Feed', brand: 'ABC Premium', unit: '50kg Bag', standardPrice: 115000, currency: 'UGX', minOrderQty: 50, leadTimeDays: 2, lastPurchasePrice: 112000, lastPurchaseDate: '2026-08-15' },
      { id: 'p-002', supplierId: 'sup-001', productName: 'Broiler Grower Feed', supplierCode: 'ABC-BG-50', category: 'Feed', brand: 'ABC Premium', unit: '50kg Bag', standardPrice: 118000, currency: 'UGX', minOrderQty: 50, leadTimeDays: 2, lastPurchasePrice: 115000, lastPurchaseDate: '2026-08-20' },
      { id: 'p-003', supplierId: 'sup-001', productName: 'Broiler Finisher Feed', supplierCode: 'ABC-BF-50', category: 'Feed', brand: 'ABC Premium', unit: '50kg Bag', standardPrice: 125000, currency: 'UGX', minOrderQty: 50, leadTimeDays: 2, lastPurchasePrice: 121000, lastPurchaseDate: '2026-08-28' },
      { id: 'p-004', supplierId: 'sup-001', productName: 'Layer Mash', supplierCode: 'ABC-LM-50', category: 'Feed', brand: 'ABC Premium', unit: '50kg Bag', standardPrice: 108000, currency: 'UGX', minOrderQty: 100, leadTimeDays: 3 },
    ],
    performance: { supplierId: 'sup-001', qualityScore: 96, deliveryScore: 94, priceScore: 88, complianceScore: 98, communicationScore: 92, supportScore: 90, invoiceAccuracyScore: 97, overallScore: 94, onTimeDeliveryPct: 94, acceptanceRate: 98, totalOrders: 48, lateDeliveries: 3, rejectedDeliveries: 1 },
    createdAt: '2022-01-15T08:00:00Z',
    updatedAt: '2026-08-28T14:00:00Z',
  },
  {
    id: 'sup-002',
    supplierCode: 'SUP-00002',
    name: 'Eastern Hatcheries Ltd',
    tradingName: 'Eastern Hatcheries',
    supplierType: 'Hatchery',
    categories: ['Day-Old Chicks', 'Pullets', 'Breeding Stock'],
    registrationNumber: 'UG-REG-2015-0089',
    taxNumber: 'TIN-1003456789',
    country: 'Uganda',
    district: 'Jinja',
    city: 'Jinja',
    physicalAddress: 'Km 5, Jinja-Kampala Road, Jinja',
    email: 'orders@easternhatcheries.co.ug',
    phone: '+256 702 345 678',
    paymentTerms: '14 Days',
    creditDays: 14,
    creditLimit: 30000000,
    currency: 'UGX',
    overallScore: 88,
    rating: 4.5,
    preferred: false,
    complianceStatus: 'compliant',
    status: 'active',
    totalSpend: 95000000,
    outstandingBalance: 8500000,
    openPOs: 1,
    onTimeDeliveryPct: 91,
    qualityScore: 93,
    lastPurchaseDate: '2026-08-20',
    contacts: [
      { id: 'c-003', supplierId: 'sup-002', fullName: 'Waiswa Henry', jobTitle: 'Sales Director', phone: '+256 702 345 678', email: 'henry@easternhatcheries.co.ug', isPrimary: true },
    ],
    documents: [
      { id: 'd-004', supplierId: 'sup-002', documentType: 'Veterinary License', documentNumber: 'VL-2026-0078', issueDate: '2026-01-01', expiryDate: '2026-10-15', status: 'expiring_soon' },
      { id: 'd-005', supplierId: 'sup-002', documentType: 'Trading License', documentNumber: 'TL-2026-0234', issueDate: '2026-01-01', expiryDate: '2026-12-31', status: 'valid' },
    ],
    products: [
      { id: 'p-005', supplierId: 'sup-002', productName: 'Broiler Day-Old Chicks (Ross 308)', supplierCode: 'EH-DOC-R308', category: 'Day-Old Chicks', brand: 'Ross', unit: 'Chick', standardPrice: 3500, currency: 'UGX', minOrderQty: 500, leadTimeDays: 7, lastPurchasePrice: 3200, lastPurchaseDate: '2026-08-20' },
      { id: 'p-006', supplierId: 'sup-002', productName: 'Layer Day-Old Chicks (Lohmann Brown)', supplierCode: 'EH-DOC-LB', category: 'Day-Old Chicks', brand: 'Lohmann', unit: 'Chick', standardPrice: 4200, currency: 'UGX', minOrderQty: 500, leadTimeDays: 7 },
    ],
    performance: { supplierId: 'sup-002', qualityScore: 93, deliveryScore: 91, priceScore: 85, complianceScore: 90, communicationScore: 88, supportScore: 86, invoiceAccuracyScore: 94, overallScore: 88, onTimeDeliveryPct: 91, acceptanceRate: 96, totalOrders: 24, lateDeliveries: 2, rejectedDeliveries: 1 },
    createdAt: '2022-06-10T08:00:00Z',
    updatedAt: '2026-08-20T10:00:00Z',
  },
  {
    id: 'sup-003',
    supplierCode: 'SUP-00003',
    name: 'VetCare Uganda Ltd',
    tradingName: 'VetCare',
    supplierType: 'Veterinary Supplier',
    categories: ['Vaccines', 'Veterinary Medicine', 'Disinfectants'],
    registrationNumber: 'UG-REG-2017-0156',
    taxNumber: 'TIN-1004567890',
    country: 'Uganda',
    district: 'Kampala',
    city: 'Kampala',
    physicalAddress: 'Plot 12, Nakasero Road, Kampala',
    email: 'sales@vetcareuganda.co.ug',
    phone: '+256 703 456 789',
    paymentTerms: '7 Days',
    creditDays: 7,
    creditLimit: 15000000,
    currency: 'UGX',
    overallScore: 96,
    rating: 4.8,
    preferred: true,
    complianceStatus: 'compliant',
    status: 'preferred',
    totalSpend: 42000000,
    outstandingBalance: 3200000,
    openPOs: 1,
    onTimeDeliveryPct: 97,
    qualityScore: 99,
    lastPurchaseDate: '2026-09-01',
    contacts: [
      { id: 'c-004', supplierId: 'sup-003', fullName: 'Dr. Apio Christine', jobTitle: 'Technical Sales Manager', phone: '+256 703 456 789', email: 'christine@vetcareuganda.co.ug', isPrimary: true },
    ],
    documents: [
      { id: 'd-006', supplierId: 'sup-003', documentType: 'Veterinary License', documentNumber: 'VL-2026-0099', issueDate: '2026-01-01', expiryDate: '2027-01-01', status: 'valid' },
      { id: 'd-007', supplierId: 'sup-003', documentType: 'Manufacturer Authorization', documentNumber: 'MA-2026-0045', issueDate: '2026-01-01', expiryDate: '2027-06-30', status: 'valid' },
    ],
    products: [
      { id: 'p-007', supplierId: 'sup-003', productName: 'Newcastle Disease Vaccine (La Sota)', supplierCode: 'VC-NDV-LS', category: 'Vaccines', brand: 'Intervet', unit: 'dose', standardPrice: 1200, currency: 'UGX', minOrderQty: 1000, leadTimeDays: 3, lastPurchasePrice: 1150, lastPurchaseDate: '2026-09-01' },
      { id: 'p-008', supplierId: 'sup-003', productName: 'Gumboro Vaccine (IBD)', supplierCode: 'VC-IBD-01', category: 'Vaccines', brand: 'Intervet', unit: 'dose', standardPrice: 1500, currency: 'UGX', minOrderQty: 1000, leadTimeDays: 3 },
      { id: 'p-009', supplierId: 'sup-003', productName: 'Marek\'s Disease Vaccine', supplierCode: 'VC-MDV-01', category: 'Vaccines', brand: 'Zoetis', unit: 'dose', standardPrice: 2200, currency: 'UGX', minOrderQty: 500, leadTimeDays: 5 },
    ],
    performance: { supplierId: 'sup-003', qualityScore: 99, deliveryScore: 97, priceScore: 90, complianceScore: 99, communicationScore: 96, supportScore: 95, invoiceAccuracyScore: 98, overallScore: 96, onTimeDeliveryPct: 97, acceptanceRate: 99, totalOrders: 36, lateDeliveries: 1, rejectedDeliveries: 0 },
    createdAt: '2021-09-20T08:00:00Z',
    updatedAt: '2026-09-01T09:00:00Z',
  },
  {
    id: 'sup-004',
    supplierCode: 'SUP-00004',
    name: 'Farm Equipment Solutions Ltd',
    tradingName: 'FES Uganda',
    supplierType: 'Equipment Supplier',
    categories: ['Poultry Equipment', 'Feeders', 'Drinkers', 'Brooders', 'Spare Parts'],
    registrationNumber: 'UG-REG-2019-0234',
    taxNumber: 'TIN-1005678901',
    country: 'Uganda',
    district: 'Wakiso',
    city: 'Entebbe',
    physicalAddress: 'Plot 78, Entebbe Road, Wakiso',
    email: 'info@fesuganda.co.ug',
    phone: '+256 704 567 890',
    paymentTerms: '30 Days',
    creditDays: 30,
    creditLimit: 25000000,
    currency: 'UGX',
    overallScore: 82,
    rating: 4.3,
    preferred: false,
    complianceStatus: 'expiring_soon',
    status: 'active',
    totalSpend: 28500000,
    outstandingBalance: 5600000,
    openPOs: 1,
    onTimeDeliveryPct: 85,
    qualityScore: 88,
    lastPurchaseDate: '2026-08-10',
    contacts: [
      { id: 'c-005', supplierId: 'sup-004', fullName: 'Ssemakula John', jobTitle: 'Managing Director', phone: '+256 704 567 890', email: 'john@fesuganda.co.ug', isPrimary: true },
    ],
    documents: [
      { id: 'd-008', supplierId: 'sup-004', documentType: 'Trading License', documentNumber: 'TL-2026-0345', issueDate: '2026-01-01', expiryDate: '2026-10-31', status: 'expiring_soon' },
      { id: 'd-009', supplierId: 'sup-004', documentType: 'Certificate of Incorporation', documentNumber: 'CI-2019-0234', issueDate: '2019-05-20', expiryDate: '2029-05-20', status: 'valid' },
    ],
    products: [
      { id: 'p-010', supplierId: 'sup-004', productName: 'Automatic Poultry Feeder (50-bird)', supplierCode: 'FES-AF-50', category: 'Feeders', brand: 'FES Pro', unit: 'piece', standardPrice: 185000, currency: 'UGX', minOrderQty: 10, leadTimeDays: 7 },
      { id: 'p-011', supplierId: 'sup-004', productName: 'Nipple Drinker Line (100-bird)', supplierCode: 'FES-ND-100', category: 'Drinkers', brand: 'FES Pro', unit: 'set', standardPrice: 320000, currency: 'UGX', minOrderQty: 5, leadTimeDays: 7 },
    ],
    performance: { supplierId: 'sup-004', qualityScore: 88, deliveryScore: 85, priceScore: 82, complianceScore: 80, communicationScore: 84, supportScore: 88, invoiceAccuracyScore: 90, overallScore: 82, onTimeDeliveryPct: 85, acceptanceRate: 94, totalOrders: 18, lateDeliveries: 3, rejectedDeliveries: 1 },
    createdAt: '2023-02-14T08:00:00Z',
    updatedAt: '2026-08-10T11:00:00Z',
  },
  {
    id: 'sup-005',
    supplierCode: 'SUP-00005',
    name: 'Nile Agro Chemicals Ltd',
    tradingName: 'Nile Agro',
    supplierType: 'Distributor',
    categories: ['Disinfectants', 'Chemicals', 'Cleaning Materials', 'PPE'],
    registrationNumber: 'UG-REG-2020-0312',
    taxNumber: 'TIN-1006789012',
    country: 'Uganda',
    district: 'Kampala',
    city: 'Kampala',
    physicalAddress: 'Plot 23, Nakawa Industrial Area, Kampala',
    email: 'orders@nileagro.co.ug',
    phone: '+256 705 678 901',
    paymentTerms: 'Cash',
    currency: 'UGX',
    overallScore: 75,
    rating: 3.9,
    preferred: false,
    complianceStatus: 'pending',
    status: 'pending_approval',
    totalSpend: 8200000,
    outstandingBalance: 0,
    openPOs: 0,
    onTimeDeliveryPct: 78,
    qualityScore: 80,
    lastPurchaseDate: '2026-07-15',
    contacts: [
      { id: 'c-006', supplierId: 'sup-005', fullName: 'Nakato Prossy', jobTitle: 'Sales Representative', phone: '+256 705 678 901', email: 'prossy@nileagro.co.ug', isPrimary: true },
    ],
    documents: [
      { id: 'd-010', supplierId: 'sup-005', documentType: 'Trading License', status: 'pending_verification' },
    ],
    products: [],
    performance: { supplierId: 'sup-005', qualityScore: 80, deliveryScore: 78, priceScore: 85, complianceScore: 70, communicationScore: 75, supportScore: 72, invoiceAccuracyScore: 82, overallScore: 75, onTimeDeliveryPct: 78, acceptanceRate: 90, totalOrders: 8, lateDeliveries: 2, rejectedDeliveries: 1 },
    createdAt: '2026-06-01T08:00:00Z',
    updatedAt: '2026-08-01T10:00:00Z',
  },
];

export const MOCK_EVALUATIONS: SupplierEvaluation[] = [
  { id: 'ev-001', supplierId: 'sup-001', supplierName: 'ABC Feeds Uganda Ltd', period: 'Q2 2026', evaluator: 'Mugisha Robert', qualityScore: 96, deliveryScore: 94, priceScore: 88, complianceScore: 98, communicationScore: 92, supportScore: 90, overallScore: 94, comments: 'Consistently excellent performance. Highly reliable.', recommendations: 'Maintain preferred status. Consider framework agreement.', status: 'approved', date: '2026-07-05' },
  { id: 'ev-002', supplierId: 'sup-003', supplierName: 'VetCare Uganda Ltd', period: 'Q2 2026', evaluator: 'Dr. Apio Grace', qualityScore: 99, deliveryScore: 97, priceScore: 90, complianceScore: 99, communicationScore: 96, supportScore: 95, overallScore: 96, comments: 'Outstanding cold chain management. Zero rejections this quarter.', status: 'approved', date: '2026-07-08' },
  { id: 'ev-003', supplierId: 'sup-002', supplierName: 'Eastern Hatcheries Ltd', period: 'Q2 2026', evaluator: 'Okello James', qualityScore: 93, deliveryScore: 91, priceScore: 85, complianceScore: 90, communicationScore: 88, supportScore: 86, overallScore: 88, comments: 'Good performance. Minor delays in June due to hatchery maintenance.', status: 'approved', date: '2026-07-10' },
];

export const MOCK_INCIDENTS: SupplierIncident[] = [
  { id: 'inc-001', incidentNumber: 'INC-2026-0012', supplierId: 'sup-004', supplierName: 'Farm Equipment Solutions Ltd', date: '2026-08-05', poNumber: 'PO-2026-0089', incidentType: 'Late Delivery', severity: 'medium', description: 'Delivery of feeders was 5 days late, causing delay in house setup for new flock.', reportedBy: 'Waiswa Peter', correctiveAction: 'Supplier to improve lead time communication', status: 'resolved' },
  { id: 'inc-002', incidentNumber: 'INC-2026-0011', supplierId: 'sup-005', supplierName: 'Nile Agro Chemicals Ltd', date: '2026-07-20', incidentType: 'Poor Quality', severity: 'high', description: 'Disinfectant supplied was below specified concentration. Batch rejected.', reportedBy: 'Nakato Sarah', status: 'under_review' },
];

export const MOCK_PAYMENTS: SupplierPayment[] = [
  { id: 'pay-001', paymentNumber: 'PAY-2026-0045', supplierId: 'sup-001', supplierName: 'ABC Feeds Uganda Ltd', invoiceRef: 'INV-ABC-2026-0089', paymentDate: '2026-08-30', amount: 25000000, currency: 'UGX', paymentMethod: 'Bank Transfer', reference: 'TXN-STD-20260830-001', processedBy: 'Kato Finance' },
  { id: 'pay-002', paymentNumber: 'PAY-2026-0044', supplierId: 'sup-003', supplierName: 'VetCare Uganda Ltd', invoiceRef: 'INV-VC-2026-0034', paymentDate: '2026-09-01', amount: 3600000, currency: 'UGX', paymentMethod: 'Bank Transfer', reference: 'TXN-STD-20260901-002', processedBy: 'Kato Finance' },
];

export const MOCK_CONTRACTS: SupplierContract[] = [
  { id: 'con-001', contractNumber: 'CON-2026-0001', supplierId: 'sup-001', supplierName: 'ABC Feeds Uganda Ltd', contractType: 'Framework Agreement', startDate: '2026-01-01', endDate: '2026-12-31', value: 200000000, currency: 'UGX', paymentTerms: '30 Days', status: 'active', owner: 'Mugisha Robert' },
  { id: 'con-002', contractNumber: 'CON-2026-0002', supplierId: 'sup-003', supplierName: 'VetCare Uganda Ltd', contractType: 'Annual Supply Agreement', startDate: '2026-01-01', endDate: '2026-12-31', value: 50000000, currency: 'UGX', paymentTerms: '7 Days', status: 'active', owner: 'Dr. Apio Grace' },
];

export const SUPPLIER_CATEGORIES = [
  'Day-Old Chicks', 'Pullets', 'Breeding Stock', 'Feed', 'Feed Ingredients', 'Feed Supplements',
  'Premixes', 'Vaccines', 'Veterinary Medicine', 'Disinfectants', 'Chemicals', 'PPE',
  'Egg Trays', 'Packaging', 'Poultry Equipment', 'Feeders', 'Drinkers', 'Brooders',
  'Incubators', 'Generators', 'Spare Parts', 'Fuel', 'Cleaning Materials', 'Construction Materials',
  'Farm Equipment', 'Transport Services', 'Veterinary Services', 'Maintenance Services',
  'Professional Services', 'Office Supplies', 'Other',
];

export const SUPPLIER_TYPES = [
  'Company', 'Individual', 'Manufacturer', 'Distributor', 'Wholesaler', 'Retailer',
  'Service Provider', 'Contractor', 'Veterinary Supplier', 'Feed Supplier', 'Hatchery',
  'Equipment Supplier', 'Transport Provider', 'Other',
];

export const PAYMENT_TERMS_OPTIONS = ['Cash', 'Prepaid', 'COD', '7 Days', '14 Days', '30 Days', '45 Days', '60 Days', 'Custom'];
